import moment from 'moment';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Modal, Form, Input, message } from 'antd';
import { useState } from 'react';
import { UndoOutlined, CheckCircleOutlined } from '@ant-design/icons';
import NativeNumber from '../../component/Numeral';
import { shortAddress } from '../../methods/utils/general-utils';
import baseuri from '../../methods/baseuri';

interface ISingleFundingRequest {
	item: any;
}

const Style = styled.div`
	padding: 20px;
	border-bottom: 1px solid #f2f2f2;
	margin-bottom: 10px;
	background-color: #f8f8f8;
	color: #333;
	text-decoration: none;

	& .details {
		margin-top: 15px;
		border-top: 1px dashed #ccc;
		padding-top: 10px;

		& > * {
			display: inline-block;
			min-width: 100px;
			margin-right: 15px;
			margin-bottom: 5px;

			& p {
				font-size: 14px;
				margin: 0;
				padding: 0;

				&.label {
					color: gray;
					font-size: 14px;
					font-style: italic;
				}
			}
		}
	}
`;

const SingleFundingRequest = ({ item }: ISingleFundingRequest) => {
	const [visible, setVisible] = useState(false);
	const [processModalVisible, setProcessModalVisible] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [form] = Form.useForm();

	const handleRevert = async () => {
		try {
			const values = await form.validateFields();
			setSubmitting(true);
			await baseuri.post('/admin/funding/revert', {
				fundingRequestId: item._id,
				comment: values.comment || 'Errored Funding',
			});
			message.success('Funding correctly reverted');
			setVisible(false);
			form.resetFields();
		} catch (error: any) {
			if (error.response?.data) {
				const responseData = error.response.data;
				const errorMsg = responseData.message || 'Failed to revert funding';
				
				if (responseData.data && typeof responseData.data === 'object' && 'currentBalance' in responseData.data) {
					Modal.error({
						title: 'Revert Failed',
						content: (
							<div>
								<p>{errorMsg}</p>
								<p><strong>Current Balance:</strong> {responseData.data.currentBalance}</p>
								<p><strong>Amount to Revert:</strong> {responseData.data.amountToRevert}</p>
							</div>
						),
					});
				} else {
					message.error(errorMsg);
				}
			} else {
				message.error('An unexpected error occurred.');
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handleProcessFailedFunding = async () => {
		try {
			setProcessing(true);
			await baseuri.post('/admin/currency/process-failed-funding', {
				fundingRequestId: item._id,
			});
			message.success('Funding correctly processed and wallet credited');
			setProcessModalVisible(false);
		} catch (error: any) {
			if (error.response?.data) {
				const responseData = error.response.data;
				const errorMsg = responseData.message || 'Failed to process funding';
				message.error(errorMsg);
			} else {
				message.error('An unexpected error occurred.');
			}
		} finally {
			setProcessing(false);
		}
	};

	const fundingStatusInterpreter = (status: any) => {
		if (String(status) === '0') {
			return 'Customer Funding Pending';
		}
		if (String(status) === '1') {
			return 'Customer Funding Complete';
		}
		if (String(status) === '2') {
			return 'Failed';
		}
		if (String(status) === '3') {
			return 'Reversed';
		}
		return status;
	};

	return (
		<Style>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<div>
					<p
						style={{
							color:
								String(item.fundingStatus) === '1'
									? 'var(--green-color)'
									: '#000',
						}}
					>
						<strong>
							<NativeNumber value={item.amountReceived} decimalPlaces={2} />{' '}
							{item.currencyId?.symbol}
						</strong>
					</p>
					<p
						style={{
							color:
								String(item.fundingStatus) === '1'
									? 'var(--green-color)'
									: '#000',
						}}
					>
						{fundingStatusInterpreter(item.fundingStatus)}
					</p>
				</div>
				<div>
					<Button 
						style={{ marginRight: 10 }}
						type="primary" 
						icon={<CheckCircleOutlined />} 
						onClick={() => setProcessModalVisible(true)} 
						disabled={String(item.fundingStatus) !== '2'}
					>
						Process Failed
					</Button>
					<Button danger icon={<UndoOutlined />} onClick={() => setVisible(true)} disabled={String(item.fundingStatus) === '3' || String(item.fundingStatus) === '2' || String(item.fundingStatus) === '0'}>
						Revert
					</Button>
				</div>
			</div>
			<div className="details">
				<div>
					{item.userId?._id ? (
						<Link to={`/user-profile/${item.userId?._id}`} target="_blank">
							{item.userId?.username ? (
								<span>@{item.userId.username}</span>
							) : (
								item.userEmail
							)}
						</Link>
					) : (
						item.userEmail
					)}
					<p className="label">Customer</p>
				</div>
				<div>
					<p>{item.fundingSource}</p>
					<p className="label">Funding Source</p>
				</div>
				<div>
					<a
						href={`https://${
							item.blockchainNetworkId
								? item.blockchainNetworkId.explorerUrl
								: 'bscscan.com'
						}/tx/${item.transactionHash}`}
						target="_blank"
						rel="noreferrer"
					>
						Hash
					</a>
					<p className="label">Transaction Hash</p>
				</div>

				<div>
					<p>{item.failureReason || 'N/A'}</p>
					<p className="label">Failure Reason</p>
				</div>
				<div>
					<a
						target="_blank"
						rel="noreferrer"
						href={`https://${
							item.blockchainNetworkId
								? item.blockchainNetworkId.explorerUrl
								: 'bscscan.com'
						}/address/${item.depositAddress}#tokentxns`}
					>
						{shortAddress(item.depositAddress)}
					</a>
					<p className="label">Origin Address</p>
				</div>
				<div>
					<a
						target="_blank"
						rel="noreferrer"
						href={`https://${
							item.blockchainNetworkId
								? item.blockchainNetworkId.explorerUrl
								: 'bscscan.com'
						}/address/${item.receivingAddress}#tokentxns`}
					>
						{shortAddress(item.receivingAddress)}
					</a>
					<p className="label">Receiving Address</p>
				</div>
				<div>
					<p>{moment(item.createdAt).format('MMM DD, YY / hh:mm:ss a')}</p>
					<p className="label">Created</p>
				</div>
				<div>
					<p>{moment(item.updatedAt).format('MMM DD, YY / hh:mm:ss a')}</p>
					<p className="label">Updated</p>
				</div>
			</div>

			<Modal
				title="Revert Funding"
				visible={visible}
				onCancel={() => setVisible(false)}
				onOk={handleRevert}
				confirmLoading={submitting}
			>
				<Form form={form} layout="vertical">
					<Form.Item
						name="comment"
						label="Revert Comment / Reason"
						rules={[{ required: true, message: 'Please provide a reason to revert this funding.' }]}
					>
						<Input.TextArea placeholder="Enter comment..." />
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="Process Failed Funding"
				visible={processModalVisible}
				onCancel={() => setProcessModalVisible(false)}
				onOk={handleProcessFailedFunding}
				confirmLoading={processing}
				okText="Confirm & Process"
			>
				<p>Are you sure you want to manually process this failed funding request?</p>
				<p>This will replicate the full funding flow, credit the user wallet, and send notifications.</p>
				<div style={{ background: '#f0f2f5', padding: '10px', borderRadius: '4px', marginTop: '10px' }}>
					<p><strong>Amount:</strong> <NativeNumber value={item.amountReceived} decimalPlaces={2} /> {item.currencyId?.symbol}</p>
					<p><strong>User:</strong> {item.userEmail}</p>
					<p><strong>Hash:</strong> {item.transactionHash}</p>
				</div>
			</Modal>
		</Style>
	);
};

export default SingleFundingRequest;
